import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Video, Phone, Mail, MessageCircle, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { usePageTitle } from "@/lib/usePageTitle";
import { SITE } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROOM_IMG = "https://images.pexels.com/photos/26932519/pexels-photo-26932519.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CONTACT_OPTS = [
  { v: "call", label: "Call me back", icon: Phone, testid: "booking-contact-pref-call" },
  { v: "message", label: "Send me a message", icon: MessageCircle, testid: "booking-contact-pref-message" },
  { v: "email", label: "Email me", icon: Mail, testid: "booking-contact-pref-email" },
];

const inputCls =
  "w-full rounded-xl border border-[#E2DACD] bg-white px-4 py-3 text-sm text-[#1E2723] placeholder:text-[#A8B4AD] focus:border-[#527363]";
const labelCls = "mb-1.5 block text-sm font-semibold text-[#2A3630]";

const Booking = () => {
  usePageTitle("Book & Contact", "Request an appointment for psychotherapy or counselling — in person in Marino, Dublin 3, or online worldwide.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", session_type: "in-person", availability: "", contact_preference: "call", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const phoneNeeded = form.contact_preference !== "email";

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/enquiries`, form);
      setDone(res.data);
      toast.success("Enquiry sent — Brian will be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again, or email directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="booking-page">
      <section className="mx-auto max-w-6xl px-5 pb-16 pt-16 sm:px-8 lg:pt-24">
        <Reveal y={12}><p className="eyebrow">Booking & Contact</p></Reveal>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl font-medium leading-[1.12] tracking-tight text-[#1E2723] sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.15}>The first step is</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#527363]">simply reaching out</MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-[#4A5750] sm:text-lg">
            Send a confidential enquiry below. Brian will reply personally to arrange an initial session — in person in {SITE.location} or online.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-6xl items-start gap-10 px-5 pb-24 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
        <Reveal>
          <div className="soft-card p-7 sm:p-10">
            {done ? (
              <div className="py-10 text-center" data-testid="booking-success-message">
                <CheckCircle2 size={52} className="mx-auto text-[#527363]" strokeWidth={1.5} />
                <h2 className="mt-6 font-serif text-3xl font-medium text-[#1E2723]">Thank you, {done.name.split(" ")[0]}.</h2>
                <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#4A5750]">
                  Your enquiry has been received. Brian will be in touch shortly to arrange your {done.session_type === "online" ? "online" : "in-person"} session.
                </p>
                <p className="mt-3 text-xs uppercase tracking-wider text-[#65756C]">Reference: {done.id.slice(0, 8)}</p>
                <div className="mx-auto mt-8 max-w-md rounded-2xl border border-[#E2DACD] bg-[#F7F3EB] p-6" data-testid="booking-pay-now-card">
                  <p className="text-sm leading-relaxed text-[#4A5750]">
                    Want to take care of payment now? You can pay securely by card — no pressure, it can also wait until your session is arranged.
                  </p>
                  <Link
                    to="/pay"
                    data-testid="booking-pay-now-link"
                    className="btn-primary mt-4 w-full"
                  >
                    Pay now · {done.session_type === "online" ? "€50 online" : "€60 in person"} <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="booking-form">
                <fieldset>
                  <legend className="mb-3 text-sm font-semibold text-[#2A3630]">How would you like to attend?</legend>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { v: "in-person", label: "In person · Marino · €60", icon: MapPin, testid: "booking-session-type-in-person" },
                      { v: "online", label: "Online · Video · €50", icon: Video, testid: "booking-session-type-online" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        data-testid={o.testid}
                        onClick={() => setForm((f) => ({ ...f, session_type: o.v }))}
                        aria-pressed={form.session_type === o.v}
                        className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-4 text-sm font-semibold transition-[background-color,border-color,color] duration-300 ${
                          form.session_type === o.v
                            ? "border-[#527363] bg-[#527363] text-white"
                            : "border-[#E2DACD] bg-white text-[#3E4A43] hover:border-[#B5A793]"
                        }`}
                      >
                        <o.icon size={16} /> {o.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <div>
                  <label htmlFor="booking-message" className={labelCls}>What would you like help with? *</label>
                  <textarea id="booking-message" data-testid="booking-message-textarea" rows={4} required value={form.message} onChange={set("message")}
                    className={inputCls}
                    placeholder="A brief description — a few words is enough (e.g. anxiety, grief, feeling stuck)." />
                </div>

                <div>
                  <label htmlFor="booking-availability" className={labelCls}>When would you be available for sessions? *</label>
                  <input id="booking-availability" data-testid="booking-availability-input" required value={form.availability} onChange={set("availability")}
                    className={inputCls} placeholder="e.g. weekday evenings, or Saturday mornings" />
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="booking-name" className={labelCls}>Full name *</label>
                    <input id="booking-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")}
                      className={inputCls} placeholder="Your name" />
                  </div>
                  <div>
                    <label htmlFor="booking-email" className={labelCls}>Email *</label>
                    <input id="booking-email" data-testid="booking-email-input" type="email" required value={form.email} onChange={set("email")}
                      className={inputCls} placeholder="you@example.ie" />
                  </div>
                </div>

                <fieldset>
                  <legend className="mb-3 text-sm font-semibold text-[#2A3630]">How would you like Brian to respond?</legend>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {CONTACT_OPTS.map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        data-testid={o.testid}
                        onClick={() => setForm((f) => ({ ...f, contact_preference: o.v }))}
                        aria-pressed={form.contact_preference === o.v}
                        className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-3.5 text-sm font-semibold transition-[background-color,border-color,color] duration-300 ${
                          form.contact_preference === o.v
                            ? "border-[#527363] bg-[#E3ECE7] text-[#1E2723]"
                            : "border-[#E2DACD] bg-white text-[#3E4A43] hover:border-[#B5A793]"
                        }`}
                      >
                        <o.icon size={16} /> {o.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <div>
                  <label htmlFor="booking-phone" className={labelCls}>Phone {phoneNeeded ? "*" : "(optional)"}</label>
                  <input id="booking-phone" data-testid="booking-phone-input" type="tel" required={phoneNeeded} value={form.phone} onChange={set("phone")}
                    className={inputCls} placeholder="So Brian can call or message you back" />
                </div>

                <button type="submit" data-testid="submit-booking-button" disabled={sending} className="btn-primary w-full disabled:opacity-60">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {sending ? "Sending…" : "Send confidential enquiry"}
                </button>
                <p className="text-center text-xs leading-relaxed text-[#65756C]">
                  Your enquiry is confidential and handled in line with the practice’s privacy policy.
                </p>
              </form>
            )}
          </div>
        </Reveal>

        <div className="space-y-6">
          <Reveal delay={0.1}>
            <div className="soft-card overflow-hidden" data-testid="location-card">
              <div className="h-44 overflow-hidden">
                <img src={ROOM_IMG} alt="The counselling room at the Marino practice" className="h-full w-full object-cover" />
              </div>
              <div className="p-6">
                <h2 className="flex items-center gap-2 font-serif text-xl font-medium text-[#1E2723]">
                  <MapPin size={18} className="text-[#B85C43]" /> In person · {SITE.location}
                </h2>
                <p className="mt-2 text-sm font-semibold text-[#2A3630]" data-testid="practice-address">{SITE.address}</p>
                <p className="mt-3 text-sm leading-relaxed text-[#54645B]">
                  Sessions take place at the practice — a quiet, comfortable room within easy reach of Marino, Clontarf, Fairview and Drumcondra. Full arrival details are shared when your appointment is confirmed.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="soft-card p-6" data-testid="direct-contact-card">
              <h2 className="font-serif text-xl font-medium text-[#1E2723]">Prefer to make direct contact?</h2>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E3ECE7] text-[#527363]"><Phone size={15} /></span>
                  <a data-testid="booking-phone-link" href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="font-medium text-[#2A3630] hover:text-[#B85C43]">{SITE.phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E3ECE7] text-[#527363]"><Mail size={15} /></span>
                  <a data-testid="booking-email-link" href={`mailto:${SITE.email}`} className="font-medium text-[#2A3630] hover:text-[#B85C43] break-all">{SITE.email}</a>
                </li>
              </ul>
              <p className="mt-5 rounded-xl bg-[#F8EBE7] p-4 text-xs leading-relaxed text-[#8A4A37]">
                This practice is not an emergency service. If you are in crisis, call Samaritans free on 116 123 or emergency services on 112 / 999.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Booking;
